import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HiOutlineSquares2X2, HiOutlinePhoto, HiOutlinePaintBrush } from 'react-icons/hi2'

const API_URL = import.meta.env.VITE_API_URL
const SERIES_CARD_DESCRIPTION_MAX_LENGTH = 180

function Series() {
  const { t, i18n } = useTranslation()
  const [series, setSeries] = useState([])
  const [paintings, setPaintings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/series`).then(res => res.json()),
      fetch(`${API_URL}/paintings`).then(res => res.json())
    ])
      .then(([seriesData, paintingsData]) => {
        setSeries(seriesData || [])
        setPaintings(paintingsData || [])
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  // Serien-Text nach Sprache (mit Zeichenbegrenzung für die Card)
  const getSeriesDescriptionForCard = (serie) => {
    const text = i18n.language.startsWith('de')
      ? (serie.description_de || serie.description_en || '')
      : (serie.description_en || serie.description_de || '')
    if (!text) return ''
    return text.length > SERIES_CARD_DESCRIPTION_MAX_LENGTH
      ? text.slice(0, SERIES_CARD_DESCRIPTION_MAX_LENGTH).trim() + '…'
      : text
  }

  // Helper: get main image src from painting.images
  const getMainImageSrc = (painting) => {
    if (!painting || !painting.images || painting.images.length === 0) return null
    const mainImg = painting.images.find(img => img.is_main) || painting.images[0]
    return {
      thumbSrc: mainImg ? `${API_URL}${mainImg.thumb_path || mainImg.file_path}` : null,
      fullSrc: mainImg ? `${API_URL}${mainImg.file_path}` : null
    }
  }

  // Get paintings for a series
  const getPaintingsForSeries = (seriesId) => {
    return paintings.filter(p => p.serie_id === seriesId)
  }

  if (loading) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100"></div>
    </main>
  )

  if (error) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="text-center text-red-500 dark:text-red-400 p-8">
        {t('error.loading')}
      </div>
    </main>
  )

  return (
    <div className="min-h-screen w-screen bg-white dark:bg-zinc-900 transition-colors duration-300">
      {/* Series Section */}
      <section className="w-full py-12 px-4 md:px-12 lg:px-24">
        <div className="w-full max-w-5xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('series.title')}
          </h1>
          <div className="h-px w-16 bg-gray-300 dark:bg-zinc-700 mb-8" />
          {series.length === 0 ? (
            <div className="text-center p-8">
              <p className="text-gray-500 mb-4 text-lg dark:text-gray-400">{t("no_series")}</p>
              <p className="text-sm text-gray-400">{t("add_first_series")}</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {series.map((serie) => {
              const seriesPaintings = getPaintingsForSeries(serie.id)
              const cardDescription = getSeriesDescriptionForCard(serie)

              return (
                <Link
                  key={serie.id}
                  to={`/series/${serie.id}`}
                  className="relative bg-white dark:bg-zinc-800 border-2 border-violet-400 dark:border-violet-600 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 p-6 flex flex-col gap-2 items-start text-left focus:outline-none focus:ring-2 focus:ring-violet-400 dark:focus:ring-violet-500"
                  style={{ textDecoration: 'none' }}
                  tabIndex={0}
                >
                  {/* Series Badge */}
                  <span className="absolute top-3 right-3 bg-violet-400 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow">
                    <HiOutlineSquares2X2 className="inline-block" /> {t('series.badge')}
                  </span>

                  {/* Serien-Text mit Zeichenbegrenzung (statt Bilder) */}
                  <div className="w-full min-h-[6rem] mt-5 pr-16">
                    {cardDescription ? (
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        {cardDescription}
                      </p>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-500 text-sm italic">
                        {t('series.no_description', 'Keine Beschreibung')}
                      </p>
                    )}
                  </div>

                  {/* Series Info */}
                  <h3 className="font-bold text-xl md:text-2xl text-gray-900 dark:text-gray-100 mb-1 mt-2">
                    {serie.name}
                  </h3>

                  {/* Paintings Count */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <HiOutlinePhoto className="inline-block text-base align-middle" />
                    {seriesPaintings.length} {t('series.paintings')}
                  </div>

                  {/* Related Paintings */}
                  {seriesPaintings.length > 0 && (
                    <div className="mt-3 w-full">
                      <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center gap-1">
                        <HiOutlinePaintBrush className="inline-block text-base align-middle" /> {t('series.relatedPaintings')}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {seriesPaintings.map((painting) => {
                          const imgSrc = getMainImageSrc(painting)
                          return (
                            <Link
                              key={painting.id}
                              to={`/gallery/${painting.id}`}
                              className="flex items-center gap-1 underline text-sm text-gray-700 dark:text-gray-200 hover:text-black dark:hover:text-white transition"
                              onClick={e => e.stopPropagation()}
                            >
                              {imgSrc && (
                                <img src={imgSrc.thumbSrc} alt={painting.name} className="w-7 h-7 object-cover rounded border border-gray-200 dark:border-zinc-700" />
                              )}
                              {painting.name}
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default Series 